'use client';

import { useState, useEffect } from 'react';
import { Link, useLocation } from 'wouter';
import { motion, AnimatePresence } from 'framer-motion';

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesDropdownOpen, setServicesDropdownOpen] = useState(false);
  const [coursesDropdownOpen, setCoursesDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [location] = useLocation();

  // Import the debounce utility from utils
  // Handle scroll event to change header appearance with debounce for performance
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    // Debounced version of the scroll handler for better performance
    const debouncedHandleScroll = (e: Event) => {
      // Use requestAnimationFrame for smoother performance
      requestAnimationFrame(() => {
        handleScroll();
      });
    };

    window.addEventListener('scroll', debouncedHandleScroll, { passive: true });
    return () => window.removeEventListener('scroll', debouncedHandleScroll);
  }, []);

  // Close mobile menu on navigation
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  // Toggle mobile accordion items
  const toggleMobileAccordion = (setter: React.Dispatch<React.SetStateAction<boolean>>) => {
    setter(prev => !prev);
  };

  return (
    <>
      {/* Skip to main content link for accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-primary text-white px-4 py-2 rounded-md z-50"
      >
        Skip to main content
      </a>

      <header className={`bg-white backdrop-blur-md border-b border-gray-100 sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'py-3' : 'py-4'}`} role="banner">
        <nav className="container mx-auto px-4 sm:px-6 py-2 flex justify-between items-center">
        <Link 
          href="/" 
          className="logo-link flex items-center space-x-3"
        >
          <div className="logo-gradient bg-gradient-to-tr from-blue-600 to-indigo-600 p-2 rounded-lg">
            <span className="text-white font-bold text-xl">SH</span>
          </div>
        </Link>

        <div className="hidden lg:flex space-x-8 xl:space-x-10 items-center">
          <div className="relative group">
            <Link 
              href="/" 
              className={`text-gray-700 hover:text-sky-500 transition-colors duration-200 text-sm font-medium tracking-wide ${location === '/' ? 'text-sky-500 font-medium' : ''}`}
            >
              INICIO
            </Link>
          </div>

          <div className="relative group">
            <Link 
              href="/servicios" 
              className={`text-gray-700 hover:text-sky-500 transition-colors duration-200 text-sm font-medium tracking-wide flex items-center ${location.startsWith('/servicios') ? 'text-sky-500 font-medium' : ''}`}
            >
              SERVICIOS
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <title>Flecha hacia abajo</title>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </Link>
            <div className="absolute left-0 mt-2 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 transform translate-y-2 group-hover:translate-y-0">
              <div className="py-2 bg-white rounded-md shadow-xl border border-gray-100 max-h-96 overflow-y-auto">
                <div className="px-4 py-1">
                  <div className="text-xs text-gray-500 font-semibold">SERVICIOS POPULARES</div>
                </div>
                <Link href="/servicios/clases-particulares-ia" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 mr-2.5"></span>
                  CLASES PARTICULARES
                </Link>
                <Link href="/servicios/chatbots-inteligentes-atencion-cliente" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-green-500 to-teal-500 mr-2.5"></span>
                  CHATBOTS INTELIGENTES
                </Link>
                <Link href="/servicios/inteligencia-artificial-retail-ecommerce" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-orange-500 to-red-500 mr-2.5"></span>
                  IA RETAIL & E-COMMERCE
                </Link>
                <Link href="/servicios/automatizacion-recursos-humanos-ia" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 mr-2.5"></span>
                  AUTOMATIZACIÓN RRHH
                </Link>
                
                <div className="px-4 py-1 mt-2">
                  <div className="text-xs text-gray-500 font-semibold">FONDOS Y GRANTS</div>
                </div>
                <Link href="/servicios/kit-digital-ia-formacion-empresas" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-green-600 to-emerald-600 mr-2.5"></span>
                  KIT DIGITAL
                </Link>
                <Link href="/servicios/cdti-grants-ai-implementation" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 mr-2.5"></span>
                  CDTI GRANTS
                </Link>
                <Link href="/servicios/perte-salud-ia-hospitales" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-red-600 to-pink-600 mr-2.5"></span>
                  PERTE SALUD
                </Link>
                <Link href="/servicios/nextgen-eu-chatbots-ayuntamientos" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-indigo-600 to-blue-600 mr-2.5"></span>
                  NEXTGEN EU
                </Link>
                
                <div className="px-4 py-1 mt-2">
                  <div className="text-xs text-gray-500 font-semibold">COMPLIANCE & LEGAL</div>
                </div>
                <Link href="/servicios/eu-ai-act-compliance-asesoria" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-600 to-cyan-600 mr-2.5"></span>
                  EU AI ACT
                </Link>
                <Link href="/servicios/startup-ai-investor-demo" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-orange-600 to-yellow-600 mr-2.5"></span>
                  STARTUP DEMOS
                </Link>
                
                <div className="px-4 py-1 mt-2">
                  <div className="text-xs text-gray-500 font-semibold">POR SECTOR</div>
                </div>
                <Link href="/servicios/ia-sector-inmobiliario" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200">
                  INMOBILIARIO
                </Link>
                <Link href="/servicios/ia-sector-agricola-precision" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200">
                  AGRICULTURA
                </Link>
                <Link href="/servicios/ia-sector-energetico-renovables" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200">
                  ENERGÍA
                </Link>
                <Link href="/servicios/ia-sector-turistico-hospitality" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200">
                  TURISMO
                </Link>
                
                <div className="px-4 py-1 mt-2">
                  <div className="text-xs text-gray-500 font-semibold">OTROS SERVICIOS</div>
                </div>
                <Link href="/servicios/ia-sin-programacion" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200">
                  IA SIN PROGRAMAR
                </Link>
                <Link href="/servicios/desarrollo-apps-vision-artificial" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200">
                  VISIÓN ARTIFICIAL
                </Link>
                <Link href="/servicios" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 border-t border-gray-100 mt-2 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-sky-500 to-indigo-500 mr-2.5"></span>
                  VER TODOS LOS SERVICIOS
                </Link>
              </div>
            </div>
          </div>

          <div className="relative group">
            <Link 
              href="/cursos" 
              className={`text-gray-700 hover:text-sky-500 transition-colors duration-200 text-sm font-medium tracking-wide flex items-center ${location.startsWith('/cursos') ? 'text-sky-500 font-medium' : ''}`}
            >
              CURSOS
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <title>Flecha hacia abajo</title>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </Link>
            <div className="absolute left-0 mt-2 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 transform translate-y-2 group-hover:translate-y-0">
              <div className="py-2 bg-white rounded-md shadow-xl border border-gray-100">
                <Link href="/cursos/ai-de-0-a-100" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 mr-2.5"></span>
                  AI DE 0 A 100
                </Link>
                <Link href="/cursos/agentes-ai-autonomos" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 mr-2.5"></span>
                  AGENTES AI AUTÓNOMOS
                </Link>
                <Link href="/cursos/ai-imagen-video-creacion" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 mr-2.5"></span>
                  AI IMAGEN Y VIDEO
                </Link>
                <div className="px-4 py-1">
                  <div className="text-xs text-gray-500 font-semibold">OTROS CURSOS</div>
                </div>
                <Link href="/cursos/fundamentos-ia" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200">
                  FUNDAMENTOS DE IA
                </Link>
                <Link href="/cursos/ia-generativa" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200">
                  IA GENERATIVA
                </Link>
                <Link href="/cursos" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200">
                  VER TODOS LOS CURSOS
                </Link>
              </div>
            </div>
          </div>

          <div className="relative group">
            <Link 
              href="/para-empresas"
              className={`text-gray-700 hover:text-sky-500 transition-colors duration-200 text-sm font-medium tracking-wide ${location.startsWith('/para-empresas') ? 'text-sky-500 font-medium' : ''}`}
            >
              PARA EMPRESAS
            </Link>
          </div>

          <Link 
            href="/blog" 
            className={`text-gray-700 hover:text-sky-500 transition-colors duration-200 text-sm font-medium tracking-wide ${location.startsWith('/blog') ? 'text-sky-500 font-medium' : ''}`}
          >
            BLOG
          </Link>

          <Link 
            href="/nosotros" 
            className={`text-gray-700 hover:text-sky-500 transition-colors duration-200 text-sm font-medium tracking-wide ${location === '/nosotros' ? 'text-sky-500 font-medium' : ''}`}
          >
            NOSOTROS
          </Link>

          <div className="relative group">
            <Link 
              href="/herramientas-ia" 
              className={`text-gray-700 hover:text-sky-500 transition-colors duration-200 text-sm font-medium tracking-wide flex items-center ${location.startsWith('/herramientas-ia') || location.startsWith('/tour-ia') ? 'text-sky-500 font-medium' : ''}`}
            >
              HERRAMIENTAS
              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <title>Flecha hacia abajo</title>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </Link>
            <div className="absolute left-0 mt-2 w-64 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 transform translate-y-2 group-hover:translate-y-0">
              <div className="py-2 bg-white rounded-md shadow-xl border border-gray-100">
                <Link href="/herramientas-ia" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200">
                  TODAS LAS HERRAMIENTAS
                </Link>
                <div className="px-4 py-1">
                  <div className="text-xs text-gray-500 font-semibold">EXPLORAR</div>
                </div>
                <Link href="/tour-ia" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-sky-500 mr-2.5"></span>
                  TOUR INTERACTIVO DE IA
                </Link>
                <Link href="/herramientas-ia?tab=syllabus" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-indigo-500 mr-2.5"></span>
                  GENERADOR DE SYLLABUS
                </Link>
                <Link href="/herramientas-ia?tab=pricing" className="block px-4 py-2.5 text-xs font-medium tracking-wide text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-violet-500 mr-2.5"></span>
                  CALCULADORA DE PRECIOS
                </Link>
              </div>
            </div>
          </div>

          <Link 
            href="/recursos" 
            className={`text-gray-700 hover:text-sky-500 transition-colors duration-200 text-sm font-medium tracking-wide ${location === '/recursos' ? 'text-sky-500 font-medium' : ''}`}
          >
            RECURSOS
          </Link>

          <Link 
            href="/precios" 
            className={`text-gray-700 hover:text-sky-500 transition-colors duration-200 text-sm font-medium tracking-wide ${location === '/precios' ? 'text-sky-500 font-medium' : ''}`}
          >
            PRECIOS
          </Link>

          <Link 
            href="/contacto" 
            className="bg-gradient-to-r from-sky-500 to-indigo-500 text-white font-medium text-sm py-2.5 px-6 rounded-md transition duration-300 hover:shadow-lg hover:from-sky-600 hover:to-indigo-600"
          >
            CONTACTO
          </Link>
        </div>

        <div className="lg:hidden flex items-center">
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)} 
            className="text-gray-700 hover:text-sky-500 focus:outline-none p-2.5 rounded-md hover:bg-gray-50 transition-colors duration-200 focus:ring-2 focus:ring-sky-500/20 border border-gray-100"
            aria-label="Toggle mobile menu"
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <title>Icono de cierre</title>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <title>Icono de menú</title>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </nav>

      {/* Mobile menu - modern minimalist design */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Background overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.5 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden fixed inset-0 bg-black z-30"
              onClick={() => setMobileMenuOpen(false)}
              aria-hidden="true"
            />

            {/* Menu content */}
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              className="lg:hidden bg-white backdrop-blur-md shadow-lg fixed top-[73px] left-0 right-0 z-40 overflow-y-auto border-t border-gray-100 max-h-[calc(100vh-73px)]"
            >
            <Link 
              href="/" 
              className="block px-6 py-4 text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 text-sm font-medium tracking-wide border-b border-gray-100"
            >
              INICIO
            </Link>

            {/* Mobile Servicios Accordion */}
            <div className="mobile-accordion">
              <div 
                onClick={() => toggleMobileAccordion(setServicesDropdownOpen)}
                className="mobile-accordion-header flex justify-between items-center px-6 py-4 text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 cursor-pointer text-sm font-medium tracking-wide border-b border-gray-100"
              >
                <span>SERVICIOS</span>
                <svg
                  className={`w-4 h-4 transform transition-transform duration-200 ${servicesDropdownOpen ? 'rotate-180' : 'rotate-0'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <title>Flecha desplegable</title>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              <motion.div
                initial={false}
                animate={{
                  height: servicesDropdownOpen ? 'auto' : 0,
                  opacity: servicesDropdownOpen ? 1 : 0,
                  display: servicesDropdownOpen ? 'block' : 'none'
                }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden bg-gray-50 border-b border-gray-100 max-h-80 overflow-y-auto"
              >
                <div className="px-10 py-2">
                  <div className="text-xs text-gray-500 font-semibold">POPULARES</div>
                </div>
                <Link href="/servicios/clases-particulares-ia" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 mr-2.5"></span>
                  CLASES PARTICULARES
                </Link>
                <Link href="/servicios/chatbots-inteligentes-atencion-cliente" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-green-500 to-teal-500 mr-2.5"></span>
                  CHATBOTS INTELIGENTES
                </Link>
                <Link href="/servicios/inteligencia-artificial-retail-ecommerce" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-orange-500 to-red-500 mr-2.5"></span>
                  IA RETAIL & E-COMMERCE
                </Link>
                <Link href="/servicios/automatizacion-recursos-humanos-ia" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 mr-2.5"></span>
                  AUTOMATIZACIÓN RRHH
                </Link>
                
                <div className="px-10 py-2 mt-2">
                  <div className="text-xs text-gray-500 font-semibold">FONDOS Y GRANTS</div>
                </div>
                <Link href="/servicios/kit-digital-ia-formacion-empresas" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-green-600 to-emerald-600 mr-2.5"></span>
                  KIT DIGITAL
                </Link>
                <Link href="/servicios/cdti-grants-ai-implementation" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 mr-2.5"></span>
                  CDTI GRANTS
                </Link>
                <Link href="/servicios/perte-salud-ia-hospitales" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-red-600 to-pink-600 mr-2.5"></span>
                  PERTE SALUD
                </Link>
                <Link href="/servicios/nextgen-eu-chatbots-ayuntamientos" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-indigo-600 to-blue-600 mr-2.5"></span>
                  NEXTGEN EU
                </Link>
                
                <div className="px-10 py-2 mt-2">
                  <div className="text-xs text-gray-500 font-semibold">COMPLIANCE</div>
                </div>
                <Link href="/servicios/eu-ai-act-compliance-asesoria" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-600 to-cyan-600 mr-2.5"></span>
                  EU AI ACT
                </Link>
                <Link href="/servicios/startup-ai-investor-demo" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-orange-600 to-yellow-600 mr-2.5"></span>
                  STARTUP DEMOS
                </Link>
                
                <div className="px-10 py-2 mt-2">
                  <div className="text-xs text-gray-500 font-semibold">POR SECTOR</div>
                </div>
                <Link href="/servicios/ia-sector-inmobiliario" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500">
                  INMOBILIARIO
                </Link>
                <Link href="/servicios/ia-sector-agricola-precision" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500">
                  AGRICULTURA
                </Link>
                <Link href="/servicios/ia-sector-energetico-renovables" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500">
                  ENERGÍA
                </Link>
                <Link href="/servicios/ia-sector-turistico-hospitality" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500">
                  TURISMO
                </Link>
                <Link href="/servicios/ia-optimizacion-logistica-transporte" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500">
                  LOGÍSTICA
                </Link>
                
                <div className="px-10 py-2 mt-2">
                  <div className="text-xs text-gray-500 font-semibold">OTROS</div>
                </div>
                <Link href="/servicios/ia-sin-programacion" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500">
                  IA SIN PROGRAMAR
                </Link>
                <Link href="/servicios/desarrollo-apps-vision-artificial" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500">
                  VISIÓN ARTIFICIAL
                </Link>
                <Link href="/servicios" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500 border-t border-gray-200 mt-2 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-sky-500 to-indigo-500 mr-2.5"></span>
                  VER TODOS LOS SERVICIOS
                </Link>
              </motion.div>
            </div>

            {/* Mobile Cursos Accordion */}
            <div className="mobile-accordion">
              <div 
                onClick={() => toggleMobileAccordion(setCoursesDropdownOpen)}
                className="mobile-accordion-header flex justify-between items-center px-6 py-4 text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 cursor-pointer text-sm font-medium tracking-wide border-b border-gray-100"
              >
                <span>CLASES</span>
                <svg
                  className={`w-4 h-4 transform transition-transform duration-200 ${coursesDropdownOpen ? 'rotate-180' : 'rotate-0'}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                  aria-hidden="true"
                >
                  <title>Flecha desplegable</title>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>

              <motion.div
                initial={false}
                animate={{
                  height: coursesDropdownOpen ? 'auto' : 0,
                  opacity: coursesDropdownOpen ? 1 : 0,
                  display: coursesDropdownOpen ? 'block' : 'none'
                }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden bg-gray-50 border-b border-gray-100"
              >
                <Link href="/cursos/ai-de-0-a-100" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 mr-2.5"></span>
                  AI DE 0 A 100
                </Link>
                <Link href="/cursos/agentes-ai-autonomos" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 mr-2.5"></span>
                  AGENTES AI AUTÓNOMOS
                </Link>
                <Link href="/cursos/ai-imagen-video-creacion" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500 flex items-center">
                  <span className="w-2 h-2 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 mr-2.5"></span>
                  AI IMAGEN Y VIDEO
                </Link>
                <Link href="/cursos/fundamentos-ia" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500">
                  FUNDAMENTOS DE IA
                </Link>
                <Link href="/cursos/ia-generativa" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500">
                  IA GENERATIVA
                </Link>
                <Link href="/cursos" className="block px-10 py-3.5 text-xs font-medium tracking-wide text-gray-600 hover:bg-gray-100 hover:text-sky-500 transition-colors duration-200 border-l border-transparent hover:border-sky-500">
                  VER TODOS LOS CURSOS
                </Link>
              </motion.div>
            </div>

            <Link 
              href="/para-empresas" 
              className="block px-6 py-4 text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 text-sm font-medium tracking-wide border-b border-gray-100"
            >
              PARA EMPRESAS
            </Link>

            <Link 
              href="/blog" 
              className="block px-6 py-4 text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 text-sm font-medium tracking-wide border-b border-gray-100"
            >
              BLOG
            </Link>

            <Link 
              href="/nosotros" 
              className="block px-6 py-4 text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 text-sm font-medium tracking-wide border-b border-gray-100"
            >
              NOSOTROS
            </Link>

            <Link 
              href="/recursos" 
              className="block px-6 py-4 text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 text-sm font-medium tracking-wide border-b border-gray-100"
            >
              RECURSOS
            </Link>

            <Link 
              href="/precios" 
              className="block px-6 py-4 text-gray-700 hover:bg-gray-50 hover:text-sky-500 transition-colors duration-200 text-sm font-medium tracking-wide border-b border-gray-100"
            >
              PRECIOS
            </Link>

            <Link 
              href="/contacto" 
              className="block mx-6 my-4 py-3.5 bg-gradient-to-r from-sky-500 to-indigo-500 text-white font-medium transition-all duration-300 text-center rounded-md shadow-md hover:shadow-lg hover:from-sky-600 hover:to-indigo-600 text-sm tracking-wide"
            >
              CONTACTO
            </Link>
          </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  </>
  );
}